import React from 'react';
import type { DraggableProvided } from 'react-beautiful-dnd';
import { Draggable } from 'react-beautiful-dnd';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import Styles from './VendorUserList.module.scss';
import type { VendorUser } from '~/models/VendorUser';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { useVendorUsers } from '~/hooks/vendors/useVendorUsers';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { VendorUserModal } from '~/components/specifics/vendors/settings/vendor_users/VendorUserModal';
import { destroy } from '~/utils/api/vendors/VendorUser';
import { isSuccess } from '~/utils/api/api_handler';

type Props = {
  vendorUser: VendorUser;
  index: number;
};

export const VendorUserListRow: React.FC<Props> = ({
  vendorUser,
  index
}: Props) => {
  const { data: vendorUsers, mutate: mutateVendorUsers } =
    useVendorUsers().index();
  const {
    isOpen: showUpdate,
    handleOpen: openUpdate,
    handleClose: closeUpdate
  } = useOpenHandler();
  const {
    isOpen: confirmDestroy,
    handleOpen: openConfirmDestroy,
    handleClose: closeConfirmDestroy
  } = useOpenHandler();
  const onClickEdit = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openUpdate();
    },
    [openUpdate]
  );
  const onClickDelete = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openConfirmDestroy();
    },
    [openConfirmDestroy]
  );
  const handleDeleteVendorUser = React.useCallback(async () => {
    const result = await destroy(vendorUser.id);
    if (isSuccess(result)) {
      await mutateVendorUsers?.();
      toast.success('削除しました');
      return;
    }
    toast.error(`エラーが発生しました。${result.message}`);
  }, [mutateVendorUsers, vendorUser.id]);
  if (vendorUsers === null) {
    return null;
  }
  return (
    <Draggable draggableId={String(vendorUser.id)} index={index}>
      {(provided: DraggableProvided): React.ReactElement<HTMLElement> => (
        <tr
          ref={provided.innerRef}
          className={clsx(Styles.ListRow)}
          style={provided.draggableProps.style}
          {...provided.draggableProps}
        >
          <td className={Styles.OrderHandle} {...provided.dragHandleProps}>
            <img alt="drag handle" src="/assets/DragHandle.svg" />
          </td>
          <td className={clsx(Styles.ListCol, Styles.SpCol)}>
            <div className={Styles.Name}>{vendorUser.name}</div>
            <div className={Styles.SubInfo}>{vendorUser.email}</div>
            <div className={Styles.SubInfo}>{vendorUser.tel}</div>
          </td>
          <td className={clsx(Styles.ListCol, Styles.PcCol)}>
            {vendorUser.name}
          </td>
          <td className={clsx(Styles.ListCol, Styles.PcCol)}>
            {vendorUser.email}
          </td>
          <td className={clsx(Styles.ListCol, Styles.PcCol)}>
            {vendorUser.tel}
          </td>
          <td className={clsx(Styles.ListCol, Styles.Edit)}>
            <a className={Styles.EditLink} href="#" onClick={onClickEdit}>
              編集
            </a>
            {vendorUsers.length >= 1 && (
              <a className={Styles.DeleteLink} href="#" onClick={onClickDelete}>
                削除
              </a>
            )}
            {showUpdate && (
              <VendorUserModal
                handleClose={closeUpdate}
                vendorUser={vendorUser}
              />
            )}
            {confirmDestroy && (
              <ConfirmModal
                confirmType="delete"
                handleAccept={handleDeleteVendorUser}
                handleCancel={closeConfirmDestroy}
                isOpen={confirmDestroy}
              >
                担当者を削除してよろしいですか？
              </ConfirmModal>
            )}
          </td>
        </tr>
      )}
    </Draggable>
  );
};
